/**
 * Analytics Service for Game Event Tracking
 *
 * Tracks game starts, completions, failures, and session time
 * Respects GDPR/cookie consent
 * Uses Sentry for analytics infrastructure
 */

import * as Sentry from '@sentry/react';
import { cookieConsentService } from './cookieConsentService';
import { logger } from '../utils/logger';

type GameName =
  | 'dice-match'
  | 'pips_word_puzzle'
  | 'oldschool-dungeon-escape'
  | 'pips-creature-keeper'
  | 'pips-guild-quest'
  | 'crossroads';

type GameEvent = 'start' | 'complete' | 'fail' | 'quit';

interface GameSession {
  gameName: GameName;
  startTime: number;
  sessionId: string;
}

interface GameEventData {
  gameName: GameName;
  event: GameEvent;
  sessionId?: string;
  duration?: number; // milliseconds
  score?: number;
  level?: number;
  metadata?: Record<string, unknown>;
}

// Track active game sessions
const activeSessions = new Map<GameName, GameSession>();

/**
 * Check if analytics is allowed based on user consent
 */
function isAnalyticsAllowed(): boolean {
  const hasConsent = cookieConsentService.hasConsent();
  const analyticsAllowed = cookieConsentService.isAllowed('analytics');
  return hasConsent && analyticsAllowed;
}

/**
 * Generate unique session ID
 */
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(7)}`;
}

/**
 * Track game start event
 */
export function trackGameStart(gameName: GameName, metadata?: Record<string, unknown>): string {
  if (!isAnalyticsAllowed()) {
    logger.log('[Analytics] Skipped - No consent');
    return '';
  }

  const sessionId = generateSessionId();
  const session: GameSession = {
    gameName,
    startTime: Date.now(),
    sessionId,
  };

  activeSessions.set(gameName, session);

  // Send to Sentry
  Sentry.addBreadcrumb({
    category: 'game',
    message: `Game started: ${gameName}`,
    level: 'info',
    data: {
      sessionId,
      ...metadata,
    },
  });

  logger.log(`[Analytics] Game started: ${gameName} (${sessionId})`);
  return sessionId;
}

/**
 * Track game completion event
 */
export function trackGameComplete(
  gameName: GameName,
  score?: number,
  level?: number,
  metadata?: Record<string, unknown>
): void {
  if (!isAnalyticsAllowed()) {
    logger.log('[Analytics] Skipped - No consent');
    return;
  }

  const session = activeSessions.get(gameName);
  const duration = session ? Date.now() - session.startTime : undefined;
  const sessionId = session?.sessionId;

  // Send to Sentry
  Sentry.addBreadcrumb({
    category: 'game',
    message: `Game completed: ${gameName}`,
    level: 'info',
    data: {
      sessionId,
      duration,
      score,
      level,
      ...metadata,
    },
  });

  // Also send as custom event for better querying
  Sentry.captureMessage(`Game completed: ${gameName}`, {
    level: 'info',
    contexts: {
      game: {
        name: gameName,
        event: 'complete',
        sessionId,
        duration,
        score,
        level,
        ...metadata,
      },
    },
  });

  logger.log(`[Analytics] Game completed: ${gameName} - Score: ${score}, Duration: ${duration}ms`);

  // Clean up session
  activeSessions.delete(gameName);
}

/**
 * Track game failure/loss event
 */
export function trackGameFail(
  gameName: GameName,
  reason?: string,
  metadata?: Record<string, unknown>
): void {
  if (!isAnalyticsAllowed()) {
    logger.log('[Analytics] Skipped - No consent');
    return;
  }

  const session = activeSessions.get(gameName);
  const duration = session ? Date.now() - session.startTime : undefined;
  const sessionId = session?.sessionId;

  // Send to Sentry
  Sentry.addBreadcrumb({
    category: 'game',
    message: `Game failed: ${gameName}`,
    level: 'info',
    data: {
      sessionId,
      duration,
      reason,
      ...metadata,
    },
  });

  logger.log(`[Analytics] Game failed: ${gameName} - Reason: ${reason}`);

  // Clean up session
  activeSessions.delete(gameName);
}

/**
 * Track game quit event (user exited before completing)
 */
export function trackGameQuit(gameName: GameName, metadata?: Record<string, unknown>): void {
  if (!isAnalyticsAllowed()) {
    logger.log('[Analytics] Skipped - No consent');
    return;
  }

  const session = activeSessions.get(gameName);
  const duration = session ? Date.now() - session.startTime : undefined;
  const sessionId = session?.sessionId;

  // Send to Sentry
  Sentry.addBreadcrumb({
    category: 'game',
    message: `Game quit: ${gameName}`,
    level: 'info',
    data: {
      sessionId,
      duration,
      ...metadata,
    },
  });

  logger.log(`[Analytics] Game quit: ${gameName} - Duration: ${duration}ms`);

  // Clean up session
  activeSessions.delete(gameName);
}

/**
 * Track custom game event
 */
export function trackGameEvent(
  gameName: GameName,
  eventName: string,
  metadata?: Record<string, unknown>
): void {
  if (!isAnalyticsAllowed()) {
    logger.log('[Analytics] Skipped - No consent');
    return;
  }

  const session = activeSessions.get(gameName);

  Sentry.addBreadcrumb({
    category: 'game',
    message: `${gameName}: ${eventName}`,
    level: 'info',
    data: {
      sessionId: session?.sessionId,
      ...metadata,
    },
  });

  logger.log(`[Analytics] ${gameName}: ${eventName}`, metadata);
}

/**
 * Get session duration for active game
 */
export function getSessionDuration(gameName: GameName): number | null {
  const session = activeSessions.get(gameName);
  if (!session) return null;
  return Date.now() - session.startTime;
}

/**
 * End all active sessions (e.g., on logout or app close)
 */
export function endAllSessions(): void {
  activeSessions.forEach((session, gameName) => {
    trackGameQuit(gameName, { reason: 'app_close' });
  });
  activeSessions.clear();
}
